import { createContext, useContext, useState, useCallback } from 'react';
import { reload, applyActionCode } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { authService, userService } from '../services/auth';
import type { 
  RegistrationFlowError, 
  RegistrationFlowState,
  RegistrationResult,
  VerificationResult,
  RegistrationErrorType
} from '../types/auth';

interface RegistrationFlowContextType extends RegistrationFlowState {
  startRegistration: (email: string, password: string, displayName: string) => Promise<RegistrationResult>;
  checkVerificationStatus: () => Promise<boolean>;
  handleVerificationLink: (oobCode: string) => Promise<VerificationResult>;
  resendVerificationEmail: () => Promise<void>;
  resetFlow: () => void;
}

const RegistrationFlowContext = createContext<RegistrationFlowContextType | null>(null);

export function RegistrationFlowProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<RegistrationFlowState>({
    step: 'idle',
    error: null,
    isLoading: false,
    userEmail: null,
    oobCode: null,
    verificationStatus: 'pending'
  });

  const setLoading = (isLoading: boolean) => {
    setState(prev => ({ ...prev, isLoading }));
  };

  const setError = (error: RegistrationFlowError | null) => {
    setState(prev => ({ ...prev, error, step: error ? 'error' : prev.step }));
  };

  const startRegistration = useCallback(async (
    email: string, 
    password: string, 
    displayName: string
  ): Promise<RegistrationResult> => {
    console.log('[RegistrationFlow] Starting registration...');
    setLoading(true);
    setState(prev => ({ ...prev, step: 'registering' }));

    try {
      const result = await authService.signUp({ email, password, displayName });

      if (!result.success) {
        console.error('[RegistrationFlow] Registration failed:', result.error);
        const error: RegistrationFlowError = {
          code: result.error?.code || 'unknown',
          message: result.error?.message || 'Помилка реєстрації',
          type: 'generic'
        };
        setError(error);
        return { success: false, error };
      }

      console.log('[RegistrationFlow] Registration successful, email sent');
      setState({
        step: 'email-sent',
        error: null,
        isLoading: false,
        userEmail: email,
        oobCode: null,
        verificationStatus: 'pending'
      });

      return { success: true, user: result.data };
    } catch (error) {
      console.error('[RegistrationFlow] Unexpected error:', error);
      const flowError: RegistrationFlowError = {
        code: 'unknown',
        message: 'Сталася неочікувана помилка',
        type: 'generic'
      };
      setError(flowError);
      return { success: false, error: flowError };
    } finally {
      setLoading(false);
    }
  }, []);

  const checkVerificationStatus = useCallback(async (): Promise<boolean> => {
    console.log('[RegistrationFlow] Checking verification status...');
    const currentAuth = auth;
    const currentUser = currentAuth.currentUser;

    if (!currentUser) {
      console.log('[RegistrationFlow] No current user');
      return false;
    }

    try {
      await reload(currentUser);
      const isVerified = currentUser.emailVerified;
      
      console.log('[RegistrationFlow] Email verified:', isVerified);

      if (isVerified) {
        // Update Firestore
        await userService.updateUser(currentUser.uid, { emailVerified: true });
        setState(prev => ({
          ...prev,
          step: 'verified',
          verificationStatus: 'verified'
        }));
      }

      return isVerified;
    } catch (error) {
      console.error('[RegistrationFlow] Error checking status:', error);
      return false;
    }
  }, []);

  const handleVerificationLink = useCallback(async (oobCode: string): Promise<VerificationResult> => {
    console.log('[RegistrationFlow] Handling verification link...');
    setLoading(true);
    setState(prev => ({ 
      ...prev, 
      step: 'verifying', 
      oobCode,
      error: null 
    }));

    const currentAuth = auth;

    try {
      console.log('[RegistrationFlow] Applying action code...');
      await applyActionCode(currentAuth, oobCode);
      console.log('[RegistrationFlow] Action code applied successfully');

      // Check if user is authenticated
      const currentUser = currentAuth.currentUser;
      
      if (currentUser) {
        console.log('[RegistrationFlow] User is authenticated, updating Firestore...');
        await reload(currentUser);
        await userService.updateUser(currentUser.uid, { emailVerified: true });
        
        setState(prev => ({
          ...prev,
          step: 'verified',
          verificationStatus: 'verified',
          isLoading: false
        }));
        
        return { success: true, isAuthenticated: true };
      } else {
        console.log('[RegistrationFlow] User is NOT authenticated (different device)');
        setState(prev => ({
          ...prev,
          step: 'verified',
          verificationStatus: 'verified',
          isLoading: false
        }));
        
        return { success: true, isAuthenticated: false };
      }
    } catch (error: any) {
      console.error('[RegistrationFlow] Error applying action code:', error);
      
      // Check if already verified
      if (error.code === 'auth/invalid-action-code' && currentAuth.currentUser) {
        await reload(currentAuth.currentUser);
        if (currentAuth.currentUser.emailVerified) {
          console.log('[RegistrationFlow] Email already verified');
          setState(prev => ({
            ...prev,
            step: 'verified',
            verificationStatus: 'verified',
            isLoading: false
          }));
          return { success: true, isAuthenticated: true };
        }
      }

      const errorType: RegistrationErrorType = 
        error.code === 'auth/expired-action-code' ? 'expired' :
        error.code === 'auth/invalid-action-code' ? 'invalid-code' :
        'generic';

      const errorFlow: RegistrationFlowError = {
        code: error.code || 'unknown',
        message: error.message || 'Помилка підтвердження email',
        type: errorType,
        action: errorType === 'expired' ? 'resend' : 
                errorType === 'invalid-code' ? 'login' : 
                'retry'
      };
      setError(errorFlow);
      return { success: false, isAuthenticated: false, error: errorFlow };
    } finally {
      setLoading(false);
    }
  }, []);

  const resendVerificationEmail = useCallback(async (): Promise<void> => {
    console.log('[RegistrationFlow] Resending verification email...');
    setLoading(true);

    try {
      const result = await authService.sendVerificationEmail();
      if (result.success) {
        console.log('[RegistrationFlow] Verification email resent');
        setState(prev => ({
          ...prev,
          step: 'email-sent',
          error: null
        }));
      } else {
        throw result.error;
      }
    } catch (error: any) {
      console.error('[RegistrationFlow] Error resending email:', error);
      setError({
        code: error?.code || 'unknown',
        message: error?.message || 'Помилка відправки листа',
        type: 'network',
        action: 'retry'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const resetFlow = useCallback(() => {
    console.log('[RegistrationFlow] Resetting flow');
    setState({
      step: 'idle',
      error: null,
      isLoading: false,
      userEmail: null,
      oobCode: null,
      verificationStatus: 'pending'
    });
  }, []);

  return (
    <RegistrationFlowContext.Provider
      value={{
        ...state,
        startRegistration,
        checkVerificationStatus,
        handleVerificationLink,
        resendVerificationEmail,
        resetFlow
      }}
    >
      {children}
    </RegistrationFlowContext.Provider>
  );
}

export function useRegistrationFlow() {
  const context = useContext(RegistrationFlowContext);
  if (!context) {
    throw new Error('useRegistrationFlow must be used within RegistrationFlowProvider');
  }
  return context;
}
